/**
 * usePledgePointsIntegration — Awards gamification points for pledge actions.
 */
import { usePoints, POINTS } from "@/hooks/usePoints";
import { useCallback } from "react";

/**
 * Hook to integrate reward pledges with the points system
 */
export const usePledgePointsIntegration = () => {
  const { awardPoints } = usePoints();

  // Award points when a pledge is created
  const awardPledgeCreatedPoints = useCallback(
    async (pledgeId: string, fromUserId: string) => {
      await awardPoints(
        POINTS.CREATE_PLEDGE,
        "pledge_created",
        pledgeId,
        "Created a reward pledge"
      );
    },
    [awardPoints]
  );

  // Award points when a pledge is accepted
  const awardPledgeAcceptedPoints = useCallback(
    async (pledgeId: string, toUserId: string) => {
      await awardPoints(
        POINTS.ACCEPT_PLEDGE,
        "pledge_accepted",
        pledgeId,
        "Accepted a reward pledge"
      );
    },
    [awardPoints]
  );

  // Award points when proof is submitted (first time only)
  const awardProofSubmittedPoints = useCallback(
    async (pledgeId: string, toUserId: string) => {
      // Idempotency is handled by the database function
      await awardPoints(
        POINTS.SUBMIT_PROOF_FIRST,
        "proof_submitted_first",
        pledgeId,
        "Submitted proof for reward"
      );
    },
    [awardPoints]
  );

  // Award points when proof is approved (to recipient)
  const awardProofApprovedRecipientPoints = useCallback(
    async (pledgeId: string, toUserId: string) => {
      await awardPoints(
        POINTS.PROOF_APPROVED_RECIPIENT,
        "proof_approved_recipient",
        pledgeId,
        "Proof approved - reward earned!"
      );
    },
    [awardPoints]
  );

  // Award points when proof is approved (to giver for good behavior)
  const awardProofApprovedGiverPoints = useCallback(
    async (pledgeId: string, fromUserId: string) => {
      await awardPoints(
        POINTS.PROOF_APPROVED_GIVER,
        "proof_approved_giver",
        pledgeId,
        "Approved proof - thank you for supporting!"
      );
    },
    [awardPoints]
  );

  return {
    awardPledgeCreatedPoints,
    awardPledgeAcceptedPoints,
    awardProofSubmittedPoints,
    awardProofApprovedRecipientPoints,
    awardProofApprovedGiverPoints,
  };
};
